import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import API, { getImageUrl } from '../utils/api';

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <circle cx="12" cy="12" r="4"/>
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none"/>
  </svg>
);

const YoutubeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.54 6.42a2.78 2.78 0 00-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46A2.78 2.78 0 001.46 6.42 29 29 0 001 12a29 29 0 00.46 5.58A2.78 2.78 0 003.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 001.95-1.95A29 29 0 0023 12a29 29 0 00-.46-5.58z"/>
    <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="#1A1A1A"/>
  </svg>
);

const TelegramIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22 2L11 13M22 2L15 22l-4-9-9-4 20-7z"/>
  </svg>
);

const socialLinks = [
  { Icon: FacebookIcon,  label: 'Facebook',  href: 'https://www.facebook.com/profile.php?id=61586710155207',  color: '#1877F2' },
  { Icon: InstagramIcon, label: 'Instagram', href: 'https://www.instagram.com/the_village_investors', color: '#E1306C' },
  { Icon: YoutubeIcon,   label: 'YouTube',   href: 'https://www.youtube.com/@thevillageinvestors-02',   color: '#FF0000' },
  { Icon: TelegramIcon,  label: 'Telegram',  href: 'https://t.me/thevillageinvestors',          color: '#0088CC' },
];

const Footer = () => {
  const [logo, setLogo] = useState(null);

  useEffect(() => {
    API.get('/settings/logo').then(r => {
      if (r.data?.value) setLogo(getImageUrl(r.data.value));
    }).catch(() => {});
  }, []);

  return (
    <footer className="footer">
      <div className="footer-grid">
        {/* Brand */}
        <div className="footer-brand">
          <div className="footer-logo">
            {logo && <img src={logo} alt="Logo" />}
            <div className="footer-logo-text">The Village <span>Investors</span></div>
          </div>
          <p>
            Empowering individuals to invest in what truly matters —
            themselves, the earth, their financial future, and generous giving.
          </p>

          {/* Social Icons */}
          <div style={{ marginTop: 24 }}>
            <p style={{ fontSize: '0.72rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: 12 }}>
              Follow Us
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              {socialLinks.map(({ Icon, label, href, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  title={label}
                  style={{
                    width: 38, height: 38,
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '2px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'rgba(255,255,255,0.5)',
                    transition: 'all 0.3s ease',
                    background: 'rgba(255,255,255,0.04)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.color = color;
                    e.currentTarget.style.borderColor = color;
                    e.currentTarget.style.background = `${color}18`;
                    e.currentTarget.style.transform = 'translateY(-3px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
                    e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Navigate */}
        <div>
          <h4 className="footer-heading">Navigate</h4>
          <ul className="footer-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/yourself">Yourself</Link></li>
            <li><Link to="/earth">Earth</Link></li>
            <li><Link to="/stocks">Stocks</Link></li>
            <li><Link to="/generosity">Generosity</Link></li>
          </ul>
        </div>

        {/* Resources */}
        <div>
          <h4 className="footer-heading">Resources</h4>
          <ul className="footer-links">
            <li><a href="#contact">Contact Us</a></li>
            <li><Link to="/admin/login">Admin Portal</Link></li>
          </ul>

          <h4 className="footer-heading" style={{ marginTop: 28 }}>Connect</h4>
          <ul className="footer-links">
            {socialLinks.map(({ label, href }) => (
              <li key={label}>
                <a href={href} target="_blank" rel="noreferrer">{label}</a>
              </li>
            ))}
          </ul>
        </div>

        {/* Philosophy */}
        <div>
          <h4 className="footer-heading">Philosophy</h4>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.88rem', lineHeight: 1.9, fontStyle: 'italic', fontFamily: 'Cormorant Garamond, serif', fontSize: '1.1rem' }}>
            "The village that invests in itself is the village that thrives."
          </p>
          <div style={{ marginTop: 24, padding: '16px', border: '1px solid rgba(201,168,76,0.2)', background: 'rgba(201,168,76,0.05)' }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', lineHeight: 1.7 }}>
              Invest in yourself. Invest in the earth. Invest in your future. Invest in generosity.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="footer-bottom">
        <p>&copy; {new Date().getFullYear()} The Village Investors. All rights reserved.</p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {socialLinks.map(({ Icon, label, href, color }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noreferrer"
              aria-label={label}
              style={{ color: 'rgba(255,255,255,0.3)', transition: 'color 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.color = color}
              onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
            >
              <Icon />
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
